import { camelCase } from "lodash";

import { apiBaseURL } from "../../../../config";
import { jwt } from "../../../../axios";

import { getCurrentFonts } from "./getCurrentFonts";
import { transformFontFaceSrcToProxiedAbsoluteURL } from "./transformFontFaceSrcToProxiedAbsoluteURL";

const preconnects = Array.from(document.querySelectorAll('link[rel="preconnect"]') as NodeListOf<HTMLLinkElement>)
  .map(({ href }) => new URL(href).hostname);

function transformSrc(src: string, baseURL?: string, jwt?: string | null, proxyAuthToken?: string) {
  // Transform relative URLs to absolute ones and, if we have a JWT, proxies them
  const {
    transformed: transformedWithoutAuthToken,
    relativeSources,
  } = transformFontFaceSrcToProxiedAbsoluteURL(
    src,
    baseURL,
    jwt ? `${apiBaseURL}/api/interactive_maps/proxy?jwt=${encodeURIComponent(jwt!)}&url=` : undefined,
  );

  let transformed = transformedWithoutAuthToken;

  // If we don't have a JWT, but we do have a `proxyAuthToken`, we can still
  // proxy the font, but do it only as a fallback
  if (!jwt && proxyAuthToken) {
    const { transformed: transformedWithAuthToken } = transformFontFaceSrcToProxiedAbsoluteURL(
      src,
      baseURL,
      `${apiBaseURL}/api/interactive_maps/proxy?proxy_auth=${encodeURIComponent(proxyAuthToken)}&url=`,
    );

    transformed = `${transformedWithoutAuthToken}, ${transformedWithAuthToken}`;
  }

  return { transformed, relativeSources };
}

export default async function loadFontFacesFromStyleElement(el: HTMLStyleElement, baseURL: string | undefined = undefined, proxyAuthToken: string | undefined = undefined) {
  const rules = [...(el.sheet?.cssRules ?? [])];
  const toLoad: Record<string, FontFace> = {};
  const fontFaceRules: Record<keyof typeof toLoad, CSSFontFaceRule> = {};

  rules.forEach((rule) => {
    if (!(rule instanceof CSSFontFaceRule)) return;

    const src = rule.style.getPropertyValue('src');
    const fontFamily = rule.style.getPropertyValue('font-family');

    if (!fontFamily || !src) return;

    const { transformed, relativeSources } = transformSrc(src, baseURL, jwt, proxyAuthToken);

    // If all relative sources are already preconnected, don't proxy them
    const finalSrc = relativeSources.every((s) => preconnects.includes(
      new URL(s, baseURL !== '' ? baseURL : undefined).hostname
    )) ? src : transformed;

    const descriptors: FontFaceDescriptors = Object.fromEntries(
      Array.from(rule.style) // A list of kebab-case property names declared in this rule
        .filter((prop) => !['font-family', 'src'].includes(prop))
        .map((prop) => [
          camelCase(prop.replace('font-', '')), // Remove 'font-' prefix
          rule.style.getPropertyValue(rule.style.getPropertyValue(prop)),
        ])
        .filter(([, value]) => !!value),
    );

    const unquotedFontFamily = fontFamily.replaceAll('"', '');

    const ff = new FontFace(
      unquotedFontFamily,
      finalSrc,
      descriptors,
    );

    // document.fonts.has() uses object equality, so we must check all fonts we know of
    /// on wether they have the same properties as the new one
    const haveFont = getCurrentFonts().some((current) => {
      const props = [...Object.keys(descriptors), 'family'] as unknown as (keyof FontFace)[];
      return props.every((p) => current[p] === ff[p]);
    });

    if (!haveFont) {
      toLoad[unquotedFontFamily] = ff;
      fontFaceRules[unquotedFontFamily] = rule;
    }
  });

  return Promise.all(
    Object.values(toLoad).map(
      (ff) => ff.load().then((loaded) => {
        document.fonts.add(loaded);
        return loaded;
      })
    )
  ).then((didLoad) => {
    document.fonts.dispatchEvent(new (window['FontFaceSetLoadEvent'] ?? Event)('loadingdone', { fontfaces: didLoad }));

    return Object.fromEntries(
      didLoad.map((ff) => {
        const unquotedFontFamily = ff.family.replaceAll('"', '');
        const rule = fontFaceRules[unquotedFontFamily];

        const src = rule.style.getPropertyValue('src');

        const { transformed: absoluteSrc } = transformSrc(src, baseURL, null, proxyAuthToken);

        return [
          unquotedFontFamily,
          // Ensure the CSS we store for embedding has the absolute URL
          rule.cssText.replace(src, absoluteSrc),
        ];
      }),
    );
  });
}
